import {
  RUNNER_STATS_RANGE_MS,
  RUNNER_STATS_RANGES,
  type RunnerStatsHistoryWire,
  type RunnerStatsRange,
} from '@developerz/domain';
import { createMemo, createSignal, For, type JSX, Match, Show, Switch } from 'solid-js';
import { t } from '../i18n';
import {
  type HistorySeries,
  historyPaused,
  historySegments,
  latestReading,
  partialRange,
} from '../lib/fleet-history';
import type { UtilBox } from '../lib/fleetUtilization';
import { useMachineHistory } from '../lib/machine-panel-reads';
import { absolute, ago, day, useMinuteClock } from '../lib/time';
import { LineChart } from './LineChart';
import { MachineReadouts } from './MachineReadouts';
import { Skeleton } from './Skeleton';
import { TabList, tabId, tabPanelId } from './TabList';

// The details panel's Resources section (#6565, prototype `panel()`): the three readouts,
// the range tabs and the CPU / Memory / Disk charts over 1 hour, 24 hours or 7 days, read
// from `GET /v1/runners/:id/stats` (#6558) through `useMachineHistory`.
//
// ONLY REAL POINTS (owner ruling G4): a missing bucket or a null reading is a break
// (`historySegments`), a range collected for part of its span says so ("Collecting since
// <time>. N of 24 hours so far."), and a machine still checking in whose newest sample is
// older than ten minutes says "History paused" (G20). A machine with no sample in the
// range shows "No readings yet." instead of three empty axes.
//
// A MACHINE FROM ANOTHER ORG SHOWS ITS READOUTS ONLY (G23): the stats read 404s cross-org,
// so neither the tabs nor the charts render and nothing is read.
//
// The range is the panel's, not the machine's: switching machines keeps the range picked,
// and the read is keyed by the machine id, so an answer for the machine just left never
// draws under the new one (`lib/machine-panel-reads.ts`).
//
// AGE IS MEASURED ON A LIVE CLOCK (#6988 review). "Now" is re-read every minute (the shared
// `useMinuteClock`) and on every settled read, success or failure, never pinned to the last
// success: a failing read must not freeze a checking-in machine's samples young, or "History paused" would never show.
// A failed refetch says so above the cached charts rather than hiding behind them.

const TABS = 'machine-range';

const RANGE_LABEL: Readonly<Record<RunnerStatsRange, string>> = {
  '1h': 'fleet.panel.range_1h',
  '24h': 'fleet.panel.range_24h',
  '7d': 'fleet.panel.range_7d',
};

const PARTIAL_KEY = {
  minutes: 'fleet.panel.partial_minutes',
  hours: 'fleet.panel.partial_hours',
  days: 'fleet.panel.partial_days',
} as const;

type ChartSpec = {
  readonly series: HistorySeries;
  readonly label: string;
  readonly yMax: number;
  readonly threshold: number;
};

const CHARTS: readonly ChartSpec[] = [
  { series: 'cpu_pct', label: 'fleet.panel.chart_cpu', yMax: 300, threshold: 100 },
  { series: 'mem_pct', label: 'fleet.panel.chart_memory', yMax: 100, threshold: 75 },
  { series: 'disk_pct', label: 'fleet.panel.chart_disk', yMax: 100, threshold: 80 },
];

/** The x axis's left, middle and right labels for a range. */
function xLabels(range: RunnerStatsRange, nowMs: number): readonly [string, string, string] {
  const now = t('fleet.panel.chart_x_now');
  if (range === '1h') return [t('fleet.panel.chart_x_60m'), t('fleet.panel.chart_x_30m'), now];
  if (range === '24h') return [t('fleet.panel.chart_x_24h'), t('fleet.panel.chart_x_12h'), now];
  const span = RUNNER_STATS_RANGE_MS['7d'];
  return [
    day(new Date(nowMs - span).toISOString()),
    day(new Date(nowMs - span / 2).toISOString()),
    now,
  ];
}

function legendOf(spec: ChartSpec, box: UtilBox): string {
  if (spec.series === 'mem_pct') return t('fleet.panel.chart_memory_legend');
  if (spec.series === 'disk_pct') return t('fleet.panel.chart_disk_legend');
  const cores = box.specs?.cpu_cores;
  return cores === undefined
    ? t('fleet.panel.chart_cpu_legend_plain')
    : t('fleet.panel.chart_cpu_legend', { cores: String(cores) });
}

/** The summary's "now" value: a whole percentage, or "Not reported". */
const reading = (v: number | null): string =>
  v === null
    ? t('fleet.panel.word_not_reported')
    : t('fleet.panel.chart_pct', { value: String(Math.round(v)) });

function HistoryCharts(props: {
  readonly box: UtilBox;
  readonly history: RunnerStatsHistoryWire;
  readonly nowMs: number;
}): JSX.Element {
  const partial = () => partialRange(props.history, props.nowMs);
  const paused = () => historyPaused(props.history, props.box.verdict.status, props.nowMs);
  return (
    <Show
      when={props.history.points.length > 0}
      fallback={<p class="machine-panel__note">{t('fleet.panel.no_readings')}</p>}
    >
      <Show when={partial()}>
        {(p) => (
          <p class="machine-panel__note">
            {t(PARTIAL_KEY[p().unit], {
              since: absolute(p().since_iso),
              done: String(p().done),
              total: String(p().total),
            })}
          </p>
        )}
      </Show>
      <Show when={paused()}>
        {(p) => (
          <p class="machine-panel__note">
            {t('fleet.panel.history_paused', { when: ago(p().last_iso, props.nowMs) })}
          </p>
        )}
      </Show>
      <For each={CHARTS}>
        {(spec) => {
          const label = () => t(spec.label);
          const now = () => latestReading(props.history, spec.series);
          return (
            <LineChart
              label={label()}
              legend={legendOf(spec, props.box)}
              summary={t('fleet.panel.chart_summary', {
                label: label(),
                span: t(RANGE_LABEL[props.history.range]),
                value: reading(now()),
              })}
              segments={historySegments(props.history, spec.series, spec.yMax, props.nowMs)}
              yMax={spec.yMax}
              threshold={spec.threshold}
              tone={
                spec.series === 'cpu_pct' && props.box.verdict.signals.cpu.level === 'crit'
                  ? 'danger'
                  : 'default'
              }
              xLabels={xLabels(props.history.range, props.nowMs)}
            />
          );
        }}
      </For>
    </Show>
  );
}

export function MachineResources(props: {
  readonly box: UtilBox;
  readonly token: string | null;
}): JSX.Element {
  const [range, setRange] = createSignal<RunnerStatsRange>('1h');
  const own = (): boolean => props.box.via_org === null;
  const history = useMachineHistory(() => (own() ? props.box.id : null), range, props.token);
  const minute = useMinuteClock();
  // Subscribes to the minute tick and to both settle stamps, then reads the wall clock.
  const nowMs = createMemo((): number => {
    minute();
    void history.dataUpdatedAt;
    void history.errorUpdatedAt;
    return Date.now();
  });
  return (
    <section class="machine-panel__section" aria-labelledby="machine-panel-res">
      <div class="machine-panel__res-head">
        <h3 id="machine-panel-res" class="machine-panel__h3">
          {t('fleet.panel.resources')}
        </h3>
        <Show when={own()}>
          <TabList
            label={t('fleet.panel.range_label')}
            idPrefix={TABS}
            items={RUNNER_STATS_RANGES.map((r) => ({ id: r, label: t(RANGE_LABEL[r]) }))}
            selected={range()}
            onSelect={(id) => setRange(id as RunnerStatsRange)}
          />
        </Show>
      </div>
      <MachineReadouts box={props.box} />
      <Show when={own()}>
        <div
          class="machine-panel__charts"
          role="tabpanel"
          id={tabPanelId(TABS, range())}
          aria-labelledby={tabId(TABS, range())}
        >
          <Show when={history.isError}>
            <p class="machine-panel__note" role="status">
              {t('fleet.panel.history_failed')}
            </p>
          </Show>
          <Switch>
            <Match when={history.data}>
              {(h) => <HistoryCharts box={props.box} history={h()} nowMs={nowMs()} />}
            </Match>
            <Match when={history.isError}>{null}</Match>
            <Match when={true}>
              <div role="status">
                <span class="sr-only">{t('states.loading')}</span>
                <Skeleton height="12rem" />
              </div>
            </Match>
          </Switch>
        </div>
      </Show>
    </section>
  );
}
